import React from 'react';
import {useFormContext} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  useElements,
} from '@stripe/react-stripe-js';
import {StripeCardNumberElementOptions} from '@stripe/stripe-js';
import {PaymentTypes} from '../../hooks';
import {UseStripeCardElementReturn} from '../StripeCardElement/useStripeCardElement';
import {StripeCardElementProps} from '../StripeCardElement/StripeCardElement';
import {BasePaymentForm} from './BasePaymentForm';
import {CardNumberElementWrapper} from './styled';

const cardNumberElementOptions: StripeCardNumberElementOptions = {
  style: {
    base: {
      padding: '0 16px 0 7px',
      color: '#161643',
      lineHeight: '50px',
      fontSize: '16px',
      '::placeholder': {
        fontFamily: 'Poppins-Regular, sans-serif',
        color: '#9696B9',
      },
    },
    invalid: {
      color: '#ff2467',
    },
  },
};

const WrappedCardNumberElement = React.memo(function WrappedCardNumberElement(
  props: any,
) {
  return (
    <CardNumberElementWrapper>
      <CardNumberElement
        {...props}
        options={{
          ...cardNumberElementOptions,
          disabled: props.disabled,
        }}
      />
    </CardNumberElementWrapper>
  );
});

const WrappedCardExpiryElement = React.memo(function WrappedCardExpiryElement(
  props: any,
) {
  return (
    <CardNumberElementWrapper>
      <CardExpiryElement
        {...props}
        options={{
          ...cardNumberElementOptions,
          disabled: props.disabled,
        }}
      />
    </CardNumberElementWrapper>
  );
});

const WrappedCardCvcElement = React.memo(function WrappedCardCvcElement(props: any) {
  return (
    <CardNumberElementWrapper>
      <CardCvcElement
        {...props}
        options={{
          ...cardNumberElementOptions,
          disabled: props.disabled,
        }}
      />
    </CardNumberElementWrapper>
  );
});

type StripePaymentFormProps = UseStripeCardElementReturn & {
  splitCardElementsProps: StripeCardElementProps;
  paymentType: PaymentTypes;
  disabledFields?: boolean;
  disabledSubmit?: boolean;
};
const StripePaymentForm = React.memo(function StripePaymentForm(
  props: StripePaymentFormProps,
) {
  const elements = useElements();
  const {i18n} = useTranslation();
  const {reset} = useFormContext();
  const {splitCardElementsProps, disabledFields} = props;

  React.useEffect(
    function clearStripeFormWhenChangedLanguage() {
      elements?.getElement(CardNumberElement)?.clear();
      elements?.getElement(CardExpiryElement)?.clear();
      elements?.getElement(CardCvcElement)?.clear();
      reset();
    },
    [elements, i18n.language, reset],
  );

  return (
    <BasePaymentForm
      CardNumberElement={
        <WrappedCardNumberElement {...splitCardElementsProps} disabled={disabledFields} />
      }
      ExpirationDateElement={
        <WrappedCardExpiryElement {...splitCardElementsProps} disabled={disabledFields} />
      }
      CVCElement={
        <WrappedCardCvcElement {...splitCardElementsProps} disabled={disabledFields} />
      }
      {...props}
    />
  );
});

export {StripePaymentForm};
